import { memo, useCallback, useMemo } from 'react';

import { Button, CopyText, Flex, Heading, Label, Switch, Text, TruncatedText } from '@/components/core';
import { HowToGuide } from '@/components/flow';
import { WarningDuotoneIcon } from '@/components/icons';
import { ZapierLogo } from '@/components/logos';
import { useGetAgentFromPath, useGetAppClientWithSecret } from '@/hooks';
import { useTriggerStore } from '@/store';
import { isNotConfiguredExport } from '@/utils';

export type ExportZapierConfigurationProps = object;

const ZAPIER_BASE_URI = 'https://api.zapier.com/v1/embed/cradl-ai/create';

// eslint-disable-next-line no-empty-pattern
const IExportZapierConfiguration = ({}: ExportZapierConfigurationProps = {}) => {
  const action = useTriggerStore((state) => state.action);
  const update = useTriggerStore((state) => state.updateAction);

  const { data: agent } = useGetAgentFromPath();
  const { data: appClient } = useGetAppClientWithSecret();

  const isConfigured = useMemo(() => !isNotConfiguredExport(action), [action]);

  const newZapUrl = `${ZAPIER_BASE_URI}?steps[0][app]=CradlAiCLIAPI@latest&steps[0][action]=agentRunComplete&steps[0][params][agentId]=${agent?.agentId}&steps[0][params][actionId]=${action?.actionId}`;

  const onSetupClicked = useCallback(() => {
    window.open(newZapUrl, '_blank');
  }, [newZapUrl]);

  return (
    <Flex col className="gap-md p-md grow">
      <Flex className="p-sm gap-sm bg-gray-25 items-center rounded-md border-1 border-gray-100">
        <ZapierLogo className="shrink-0" size="xl" />

        <TruncatedText className="grow-0">Zapier</TruncatedText>

        <div className="grow" />

        <Button className="shrink-0" variant="primary" onPress={onSetupClicked}>
          <Text>Open</Text>
        </Button>
      </Flex>

      <Flex col className="gap-xs">
        <Label>Enabled</Label>
        <Switch
          isSelected={(action.enabled as boolean | undefined) ?? false}
          onChange={(isSelected) => update({ enabled: isSelected })}
        />
      </Flex>

      <Flex col className="gap-xs">
        <Label>Client ID</Label>
        <CopyText value={appClient?.clientId} />
      </Flex>

      <Flex col className="gap-xs">
        <Label>Client Secret</Label>
        <CopyText isSensitive value={appClient?.clientSecret} />
      </Flex>

      {!isConfigured ? (
        <Flex variant="warning" className="p-md gap-sm rounded-lg">
          <WarningDuotoneIcon />

          <Flex col className="border-none">
            <Heading size="sm">Setup required</Heading>
            <Text>
              Make sure your Zap is connected and <strong>published</strong>.
            </Text>
          </Flex>
        </Flex>
      ) : null}

      <div className="grow" />

      <HowToGuide actionId={action.actionId} />
    </Flex>
  );
};

export const ExportZapierConfiguration = memo(IExportZapierConfiguration) as typeof IExportZapierConfiguration;
